import { classes } from "common/theme/theme";
import {
  DimensionedNumberInput,
  DimensionedNumberInputProps,
} from "common/components/DimensionedNumberInput";
import { FieldValidator, useField } from "formik";
import { FormLabel, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC, useMemo } from "react";

interface FormikDimensionedNumberInputProps {
  label: string;
  name: string;
  validate?: boolean;
  validator?: FieldValidator;
  tooltipText?: string;
  // props passed through to localized error message
  tProps?: { [key: string]: number | string };
  dimensionedNumberInputProps: Omit<
    DimensionedNumberInputProps,
    "label" | "storedValue" | "onChange" | "error"
  >;
  className?: string;
  fromStored?: (v: number | undefined) => number | undefined;
  toStored?: (v: number | undefined) => number | undefined;
}
export const FormikDimensionedNumberInput: FC<
  FormikDimensionedNumberInputProps
> = ({
  label,
  name,
  tooltipText,
  validator,
  validate = true,
  tProps,
  dimensionedNumberInputProps,
  className,
  fromStored,
  toStored,
}) => {
  const [field, meta, helpers] = useField({ name, validate: validator });
  const { t } = useTranslation();
  const { value } = meta;
  const { setTouched, setValue } = helpers;
  const { onBlur } = field;
  const labelId = useMemo(() => uuidv4(), []);
  return (
    <div className={classes("flex flex-col w-full max-w-80", className)}>
      <FormLabel id={labelId} className="flex gap-2">
        {label}
        {tooltipText && (
          <Tooltip title={tooltipText} placement="top">
            <InfoOutlinedIcon className="flex my-auto" fontSize="small" />
          </Tooltip>
        )}
      </FormLabel>
      <DimensionedNumberInput
        {...dimensionedNumberInputProps}
        label={""}
        storedValue={fromStored ? fromStored(value) : value}
        onChange={(n) => {
          setTouched(true, validate);
          setValue(toStored ? toStored(n) : n);
        }}
        textFieldProps={{
          "aria-labelledby": labelId,
          ...dimensionedNumberInputProps.textFieldProps,
          onBlur,
        }}
        error={meta.error === undefined ? undefined : t(meta.error, tProps)}
      />
    </div>
  );
};
